import { type ReactNode } from 'react';
import { X, type LucideIcon } from 'lucide-react';

// ============================================================================
// SlideOverDrawer - Main drawer container
// ============================================================================

interface SlideOverDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

const widthClasses = {
  sm: 'w-[400px]',
  md: 'w-[500px]',
  lg: 'w-[600px]',
  xl: 'w-[800px]',
  '2xl': 'w-[1000px]',
};

export function SlideOverDrawer({
  isOpen,
  onClose,
  children,
  width = 'lg',
}: SlideOverDrawerProps) {
  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 ${widthClasses[width]} max-w-full bg-white shadow-drawer z-50 transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {children}
      </div>
    </>
  );
}

// ============================================================================
// DrawerHeader - Dark header with title and close button
// ============================================================================

interface DrawerHeaderProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  badge?: ReactNode;
}

export function DrawerHeader({ title, subtitle, onClose, badge }: DrawerHeaderProps) {
  return (
    <div className="bg-slate-900 text-white p-6 flex-shrink-0">
      <div className="flex justify-between items-start">
        <div>
          {badge && <div className="mb-2">{badge}</div>}
          <h2 className="text-xl font-mono font-bold">{title}</h2>
          {subtitle && <p className="text-slate-400 text-sm mt-1">{subtitle}</p>}
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// DrawerNav - Icon navigation rail on the left side
// ============================================================================

interface DrawerNavItem {
  id: string;
  icon: LucideIcon;
  label: string;
}

interface DrawerNavProps {
  items: DrawerNavItem[];
  activeId: string;
  onChange: (id: string) => void;
}

export function DrawerNav({ items, activeId, onChange }: DrawerNavProps) {
  return (
    <div className="w-16 border-r border-slate-100 flex flex-col items-center py-4 gap-2 bg-slate-50 flex-shrink-0">
      {items.map((item) => (
        <button
          key={item.id}
          onClick={() => onChange(item.id)}
          title={item.label}
          className={`p-3 rounded-lg transition-colors relative group ${
            activeId === item.id
              ? 'bg-indigo-100 text-indigo-600'
              : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
          }`}
        >
          <item.icon size={20} strokeWidth={1.5} />
          {/* Tooltip */}
          <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
            {item.label}
          </span>
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// DrawerFooter - Sticky footer with save/cancel buttons
// ============================================================================

interface DrawerFooterProps {
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  saveLabel?: string;
  cancelLabel?: string;
}

export function DrawerFooter({
  isDirty,
  isSaving,
  onSave,
  onDiscard,
  saveLabel = 'Save Changes',
  cancelLabel = 'Cancel',
}: DrawerFooterProps) {
  if (!isDirty) return null;

  return (
    <div className="p-4 bg-white border-t border-slate-200 flex justify-end gap-3 shadow-lg flex-shrink-0">
      <button
        onClick={onDiscard}
        className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
      >
        {cancelLabel}
      </button>
      <button
        onClick={onSave}
        disabled={isSaving}
        className="px-6 py-2 text-sm font-semibold text-white rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:opacity-50 transition-all shadow-indigo"
      >
        {isSaving ? 'Saving...' : saveLabel}
      </button>
    </div>
  );
}

// ============================================================================
// DrawerBody - Scrollable content area (optional helper)
// ============================================================================

interface DrawerBodyProps {
  children: ReactNode;
  className?: string;
}

export function DrawerBody({ children, className = '' }: DrawerBodyProps) {
  return (
    <div className={`flex-1 overflow-y-auto ${className}`}>
      {children}
    </div>
  );
}

// ============================================================================
// DrawerContent - Wrapper for nav + body layout
// ============================================================================

interface DrawerContentProps {
  children: ReactNode;
}

export function DrawerContent({ children }: DrawerContentProps) {
  return <div className="flex flex-1 min-h-0">{children}</div>;
}
